<script lang="ts">
	import Label from "#lib/components/Label.svelte";
	import ColorInputV2 from "#lib/components/flow/editor/appearance/ColorInputV2.svelte";
	import { camelCase } from "#lib/string.js";
	import type { JsonValue } from "@bufbuild/protobuf";

	interface Props {
		prefix?: string;
		defaults?: Record<string, JsonValue>;
		values: Record<string, JsonValue>;
		percentage?: boolean;
		onInput?: (key: string, value: JsonValue) => void;
		onChange?: (key: string, value: JsonValue) => void;
		onReset?: (key: string) => void;
	}

	let { prefix = "", defaults = {}, values, percentage = false, onInput, onChange, onReset }: Props = $props();

	const backgroundColorId = $derived(camelCase(prefix + "BackgroundColor"));
	const borderColorId = $derived(camelCase(prefix + "BorderColor"));
	const primaryTextColorId = $derived(camelCase(prefix + "PrimaryTextColor"));
	const secondaryTextColorId = $derived(camelCase(prefix + "SecondaryTextColor"));

	function handleColorInput(key: string, value: string) {
		onInput?.(key, value);
	}

	function resetToPageStyle(key: string) {
		onReset?.(key);
	}
</script>

<div class="flex flex-col justify-between gap-2 border-l-[5px] border-l-gray-300 pl-6">
	<div class="flex flex-row gap-2">
		<div class="flex min-w-0 flex-1 flex-col gap-1">
			<Label for={backgroundColorId} class="font-medium">Background color</Label>
			<ColorInputV2 id={backgroundColorId} allowTransparent resetColor={defaults[backgroundColorId] as string} display="inline" value={(values[backgroundColorId] as string) ?? defaults[backgroundColorId]} onReset={() => resetToPageStyle(backgroundColorId)} oninput={value => handleColorInput(backgroundColorId, value)} onchange={value => onChange?.(backgroundColorId, value)} />
		</div>
		<div class="flex min-w-0 flex-1 flex-col gap-1">
			<Label for={borderColorId} class="font-medium">Border color</Label>
			<ColorInputV2 id={borderColorId} resetColor={defaults[borderColorId] as string} display="inline" value={(values[borderColorId] as string) ?? defaults[borderColorId]} onReset={() => resetToPageStyle(borderColorId)} oninput={value => handleColorInput(borderColorId, value)} onchange={value => onChange?.(borderColorId, value)} />
		</div>
	</div>
	<div class="flex flex-row gap-2">
		<div class="flex min-w-0 flex-1 flex-col gap-1">
			<Label for={primaryTextColorId} class="font-medium">Primary text color</Label>
			<ColorInputV2 id={primaryTextColorId} resetColor={defaults[primaryTextColorId] as string} display="inline" value={(values[primaryTextColorId] as string) ?? defaults[primaryTextColorId]} onReset={() => resetToPageStyle(primaryTextColorId)} oninput={value => handleColorInput(primaryTextColorId, value)} onchange={value => onChange?.(primaryTextColorId, value)} />
		</div>
		<div class="flex min-w-0 flex-1 flex-col gap-1">
			<Label for={secondaryTextColorId} class="font-medium">Secondary text color</Label>
			<ColorInputV2 id={secondaryTextColorId} resetColor={defaults[secondaryTextColorId] as string} display="inline" value={(values[secondaryTextColorId] as string) ?? defaults[secondaryTextColorId]} onReset={() => resetToPageStyle(secondaryTextColorId)} oninput={value => handleColorInput(secondaryTextColorId, value)} onchange={value => onChange?.(secondaryTextColorId, value)} />
		</div>
	</div>
	{#if percentage}
		{@const percentageDisabledColorId = prefix + "PercentageDisabledColor"}
		{@const percentageEnabledColorId = prefix + "PercentageEnabledColor"}
		<div class="flex flex-row gap-2">
			<div class="flex min-w-0 flex-1 flex-col gap-1">
				<Label for={percentageDisabledColorId} class="font-medium">Percentage disabled</Label>
				<ColorInputV2
					id={percentageDisabledColorId}
					resetColor={defaults[percentageDisabledColorId] as string}
					display="inline"
					value={(values[percentageDisabledColorId] as string) ?? defaults[percentageDisabledColorId]}
					onReset={() => resetToPageStyle(percentageDisabledColorId)}
					oninput={value => handleColorInput(percentageDisabledColorId, value)}
					onchange={value => onChange?.(percentageDisabledColorId, value)}
				/>
			</div>
			<div class="flex min-w-0 flex-1 flex-col gap-1">
				<Label for={percentageEnabledColorId} class="font-medium">Percentage enabled</Label>
				<ColorInputV2
					id={percentageEnabledColorId}
					resetColor={defaults[percentageEnabledColorId] as string}
					display="inline"
					value={(values[percentageEnabledColorId] as string) ?? defaults[percentageEnabledColorId]}
					onReset={() => resetToPageStyle(percentageEnabledColorId)}
					oninput={value => handleColorInput(percentageEnabledColorId, value)}
					onchange={value => onChange?.(percentageEnabledColorId, value)}
				/>
			</div>
		</div>
	{/if}
</div>
